import { MutationCache, QueryClient } from '@tanstack/react-query';

/**
 * The app's query client.
 *
 * Each mutation invalidates the keys it knows about, but pages read the same
 * rows under many different keys (a new reservation is in 'reservations',
 * 'reservations-enriched', 'production-reservations', the dashboard...), and
 * those hand-kept lists drift. With a five-minute staleTime, a page you open
 * right after saving something would show its old cached copy until a reload.
 *
 * So every mutation, once it settles (an error may still have written part of
 * its work), marks every query stale without refetching anything. Queries on
 * screen are left alone - they are refreshed by the mutation's own
 * invalidations, and refetching them here would fight in-progress UI like the
 * checklist - and any other page fetches fresh data as soon as it is opened.
 */
export function createQueryClient(): QueryClient {
  const client: QueryClient = new QueryClient({
    mutationCache: new MutationCache({
      onSettled: () => client.invalidateQueries({ refetchType: 'none' }),
    }),
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 1,
      },
    },
  });
  return client;
}
